import { useState } from "react";
import { useRecordContext } from "ra-core";

import { cn } from "@/lib/utils";
import { getAvatarColorClass } from "@/components/ui/avatar";
import type { Company } from "../types";

// Company avatar. Prefers an uploaded logo; otherwise falls back to the favicon
// of the company's email domain (derived in companies_summary from its
// contacts). A colored name-initial shows while the image loads or when it has
// no source / 404s. Once the image actually paints it REPLACES the initial on a
// neutral chip — layering the two would show the letter through a favicon's
// transparent padding (looked like two stacked images).
export const CompanyAvatar = (props: {
  record?: Company;
  width?: 20 | 40;
  height?: 20 | 40;
}) => {
  const { width = 40 } = props;
  const record = useRecordContext<Company>(props);
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (!record) return null;

  const logoSrc = record.logo?.src;
  // DuckDuckGo's icon service — far more reliable than favicon.show (which
  // currently 500s for most domains). Misses return 404 → the <img> errors →
  // the colored initial shows.
  const src =
    logoSrc ||
    (record.domain
      ? `https://icons.duckduckgo.com/ip3/${record.domain}.ico`
      : undefined);
  const initial = record.name?.charAt(0)?.toUpperCase() ?? "?";
  const showImg = !!src && !errored;

  return (
    <span
      title={record.name}
      className={cn(
        "relative rounded-full shrink-0 flex items-center justify-center overflow-hidden font-medium",
        width !== 40 ? "w-5 h-5 text-xs" : "w-10 h-10 text-sm",
        // Neutral gray chip once the image paints (matches the contact avatar
        // fallback) — dark/transparent PNG favicons stay visible instead of
        // vanishing into a pitch-black background. Colored initial otherwise.
        loaded ? "bg-muted" : getAvatarColorClass(initial),
      )}
    >
      {!loaded && initial}
      {showImg && (
        <img
          src={src}
          alt={record.name}
          onError={() => setErrored(true)}
          onLoad={(e) => {
            // DuckDuckGo serves a generic 48×48 chevron placeholder for domains
            // it has no real favicon for (HTTP 404, but the browser renders the
            // body and fires load, not error). Real favicons are multi-size
            // .ico. Detect the placeholder by its fixed intrinsic size and fall
            // back to the colored initial instead of showing a meaningless icon.
            const img = e.currentTarget;
            if (
              !logoSrc &&
              img.naturalWidth === 48 &&
              img.naturalHeight === 48
            ) {
              setErrored(true);
            } else {
              setLoaded(true);
            }
          }}
          // Contain on a neutral chip so both uploaded logos and favicons render
          // as a clean icon — never stacked over the name-initial.
          className="absolute inset-0 h-full w-full object-contain bg-muted"
        />
      )}
    </span>
  );
};
